<script setup>
import { displayPhone } from '@/plugins/misc'
import moment from 'moment'
import { mediaUrl } from '@core/utils/mediaUrl'
import {
  useHappeningsStore,
  useReservationsStore,
  useRestaurantsStore,
  useUserGroupsStore,
  useUsersStore,
  usePreferredStatusStore,
} from "@/stores"
import restaurntPin1 from "@/assets/images/maps/restaurant-waiter-icon.svg"
import {ref, nextTick, computed, onMounted} from "vue"
import noResEmptyState from "@/assets/images/ui/no_reservations_empty_state.jpg"
import {useRoute} from "vue-router"
import HappeningViewerAndEditor from "@core/components/HappeningViewerAndEditor.vue"
import InstantBookingSlotsCompact from "@core/components/InstantBookingSlotsCompact.vue"
import MemberBookingCard from "@core/components/MemberBookingCard.vue"
import {storeToRefs} from "pinia"
import GooglePlacesService from '@/services/GooglePlacesService'
import FriendsLikedChips from '@core/components/FriendsLikedChips.vue'
import ProfileEntriesPanel from '@core/components/ProfileEntriesPanel.vue'
import RestaurantTitleBlock from '@core/components/RestaurantTitleBlock.vue'
import AddToRequestButton from '@core/components/AddToRequestButton.vue'
import RestaurantMemberHighlights from '@core/components/RestaurantMemberHighlights.vue'
import CmsText from '@/@core/components/CmsText.vue'

const props = defineProps({
  restaurant: {
    type: Object,
  },
  userGroup: {
    type: Object
  },
  startAtTab: {
    type: String
  }
})
const emit = defineEmits(['update:modelValue', 'update:validInputs'])
const usersStore = useUsersStore()
const preferredStatusStore = usePreferredStatusStore()

const isMemberPreferred = computed(() =>
  myUser.value && myUser.value.userLevel !== 'dummy' && preferredStatusStore.isPreferredAt(props.restaurant?._id)
)

const memberPreferredGroupName = computed(() =>
  preferredStatusStore.groupNameForRestaurant(props.restaurant?._id)
)
let myUser = null
let isRunningInStandAlone = null
let isShowingPreferredRequestInfoDialog = null;
({ myUser, isRunningInStandAlone, isShowingPreferredRequestInfoDialog} = storeToRefs(usersStore))

const happeningsStore = useHappeningsStore()
let happeningsByRestaurant = null;
({ happeningsByRestaurant } = storeToRefs(happeningsStore))


const imageDialog = ref(false)
const profileViewsDialog = ref(false)

const currentImage = ref(0)
const showAllHours = ref(false)
const showAllPhotos = ref(false)
const showAllEventSpaces = ref(true)
const showBookings = ref(false)
const showFullDescription = ref(false)

// Instant bookings state
const hasInstantBookings = ref(false)
const instantBookingCount = ref(0)
const isLoadingInstantBookings = ref(false)

// State for on-demand fetched Google photo URL
const fetchedGooglePhotoUrl = ref(null)
const isFetchingGooglePhoto = ref(false)
const hasFetchedGooglePhoto = ref(false)
const imageLoadFailed = ref(false)

// Google Places service instance
const googlePlacesService = new GooglePlacesService()

const userGroupsStore = useUserGroupsStore()
let restaurantUserGroups = null;
({ restaurantUserGroups  } = storeToRefs(userGroupsStore))

const reservationsStore = useReservationsStore()
let myReservations = null
let editingReservation = null
let justBookedReservationsByRestaurant = null;
({myReservations, editingReservation, justBookedReservationsByRestaurant} = storeToRefs(reservationsStore))

const restaurantsStore = useRestaurantsStore()
let restaurants = null
let viewingDetailsRestaurantShowBookings = null
let favoriteNotes = null;
({ restaurants, viewingDetailsRestaurantShowBookings, favoriteNotes } = storeToRefs(restaurantsStore))

// Restaurant ids we've already attempted a full-details fetch for. Prevents
// updatePlaceData() from re-fetching the same restaurant in an infinite loop:
// getRestaurantById() reassigns googlePlaceDetails to a new object, which retriggers
// the googlePlaceDetails watcher -> updatePlaceData() again. If the fetched data is
// still missing opening_hours/vicinity (e.g. an admin save stripped those fields),
// hasFullDetails never becomes true and it would loop forever. See updatePlaceData().
const fullDetailsFetchedIds = new Set()

const tab = ref(props.startAtTab || 'Details')

// Whether this viewer has any Member Highlights to see here. The component
// reports it after its own authorized fetch, so the tab never appears empty.
const hasMemberHighlights = ref(false)

// The restaurant's own team always gets the tab, empty or not (Kapil,
// 2026-09-25: "any restaurant account member clicks on their profile page
// they should always see the tab"), and an empty one tells them how
// highlights get there. RM's platform admins see it as the team does.
const isRestaurantTeam = computed(() => !!myPermission.value || myUser.value?.userLevel === 'admin')

// Bumped when the member shares or makes private on My Notes, so the Member
// Highlights tab reloads (and appears) without a page refresh.
const highlightsVersion = ref(0)

const restaurantId = computed(() => props.restaurant?._id || props.restaurant?.id)

const profileViewCount = computed(() => {
  const rid = restaurantId.value
  if (!rid) return 0
  const storeRestaurant = restaurants.value.find(r => r._id === rid || r.id === rid)
  
  return storeRestaurant?.profileViewCount ?? props.restaurant?.profileViewCount ?? 0
})

// My Notes: the tab icon fills when the member has told RM anything
// about this place (newest non-contact entry, via the favoriteNotes map).
const hasNote = computed(() => {
  const id = restaurantId.value
  
  return !!(id && favoriteNotes.value[id]?.note)
})

const favorited = computed(() => {
  if (!myUser.value?._id) return false
  const rid = restaurantId.value
  if (!rid) return false
  const storeRestaurant = restaurants.value.find(r => r._id === rid || r.id === rid)
  const favoritedBy = storeRestaurant?.favoritedBy ?? props.restaurant?.favoritedBy
  if (favoritedBy && Array.isArray(favoritedBy)) {
    if (favoritedBy.some(id => id?.toString() === myUser.value._id?.toString())) return true
  }
  if (myUser.value.favoritedRestaurants?.includes(rid)) return true
  
  return false
})

//watch for change in props.tab and then update tab locally
watch(() => props.startAtTab, (newVal, oldVal) => {
  tab.value = newVal
})

// Check if restaurant has available instant booking slots
const checkInstantBookings = async () => {
  const restaurantId = props.restaurant?._id || props.restaurant?.id
  if (!restaurantId) return

  isLoadingInstantBookings.value = true
  try {
    const response = await fetch(`/instantBookingSlot/restaurant/${restaurantId}?status=available`)
    const data = await response.json()
    const slots = data.slots || []
    hasInstantBookings.value = slots.length > 0
    instantBookingCount.value = slots.length

    // Set default tab to "Book Now" if instant bookings are available and no specific tab requested
    // (treat 'Details' as a fallback that can be overridden)
    if (hasInstantBookings.value && (!props.startAtTab || props.startAtTab === 'Details')) {
      tab.value = 'PlatinumReserved'
    }
  } catch (error) {
    console.error('Error checking instant bookings:', error)
    hasInstantBookings.value = false
    instantBookingCount.value = 0
  } finally {
    isLoadingInstantBookings.value = false
  }
}

// Apply side-effects when the viewed restaurant changes: check instant bookings,
// refresh place data, and optionally auto-expand the bookings section.
const applyRestaurantChange = newRestaurant => {
  if (newRestaurant) {
    checkInstantBookings()
    updatePlaceData()

    // Notes can change server-side between opens (the RM AI saves them via
    // its tools), so refresh the store map instead of trusting the login-time
    // copy — otherwise a note added in chat doesn't show here until the
    // member happens to reload elsewhere.
    if (myUser.value?._id && myUser.value.userLevel !== 'dummy') {
      restaurantsStore.refreshFavoriteNotes()
    }
  }

  if (newRestaurant && viewingDetailsRestaurantShowBookings.value) {
    // Use setTimeout to ensure data is fully loaded
    setTimeout(() => {
      showBookings.value = true

      // Reset the flag after using it
      restaurantsStore.viewingDetailsRestaurantShowBookings = false
    }, 100)
  }
}

// React to subsequent restaurant changes...
watch(() => props.restaurant, applyRestaurantChange)

// ...and run once for the initial restaurant. This runs in onMounted rather than via
// { immediate: true } so updatePlaceData() is not invoked during setup: it touches refs
// (foundPlace, etc.) declared further down, which throws a temporal-dead-zone error
// ("Cannot access 'foundPlace' before initialization") when the profile mounts with a
// restaurant already populated (the modal/pop-up flow).
onMounted(() => {
  applyRestaurantChange(props.restaurant)
})

// Also watch for the flag changing directly (in case restaurant doesn't change)
watch(() => viewingDetailsRestaurantShowBookings.value, (shouldShow, oldValue) => {
  console.log('RestaurantProfile: Flag changed from', oldValue, 'to', shouldShow)
  if (shouldShow && props.restaurant) {
    console.log('RestaurantProfile: Flag triggered, expanding bookings for', props.restaurant.name)
    setTimeout(() => {
      showBookings.value = true
      console.log('RestaurantProfile: showBookings set to true via flag')

      // Reset the flag after using it
      restaurantsStore.viewingDetailsRestaurantShowBookings = false
    }, 100)
  }
})

const route = useRoute()
if (route.query.hasOwnProperty('claim'  )) {
  setTimeout(() => {
    console.log('claiming restaurant')
    isClaimRestaurantPageDialogOpen.value = true
  }, 500)
}

const isClaimRestaurantPageDialogOpen = ref(false)

const isInfoDialogVisible = ref(false)

const foundPlace = ref(null)

const todaysHours = ref(null)


// Map Stuff
const center = ref({
  lat: 14.5069174,
  lng: 121.0409052,
})

const markers = ref([])

const newDraft = async (market, restaurant) => {
  if (!myUser.value) {
    await usersStore.loginNewDummyUser()
  }
  await usersStore.startNewReservation({ restaurant, market })
}


// Helper function to get photo URL from photo reference
const getPhotoUrl = (photoReference, maxWidth = 400, maxHeight = 400) => {
  const apiKey = import.meta.env.VITE_APP_GOOGLE_MAPS_API_KEY
  
  return `https://d128sk95sboo22.cloudfront.net/maps/api/place/photo?maxwidth=${maxWidth}&maxheight=${maxHeight}&photo_reference=${photoReference}&key=${apiKey}`
}


//user's permission, if any, for current restaurant
const myPermission = computed(() => {
  //in restaurantUserGroups find a group where the props.restaurant._id matches and then in that groups userPermissions array find obejct that matches for email = myUser.value.email then return the "permission" key value in the same object that matched the email
  if(restaurantUserGroups.value?.length > 0){
    const theGroup = restaurantUserGroups.value.find(group => group?.restaurant?._id == props.restaurant._id)
    if(theGroup){
      const thePermission = theGroup.userPermissions.find(permission => permission.email == myUser.value.email)
      if(thePermission){
        return thePermission.permission
      }
    }
  }
})

// Computed property to get image IDs from event spaces as fallback
const eventSpaceImageIds = computed(() => {
  if (!props.restaurant?.eventSpaces?.length) return []
  
  return props.restaurant.eventSpaces
    .filter(space => space.imageId)
    .map(space => space.imageId)
})

// Computed property to get all available restaurant images with fallback to event spaces
const restaurantImageIds = computed(() => {
  if (props.restaurant?.imageIds?.length > 0) {
    return props.restaurant.imageIds
  }
  
  return eventSpaceImageIds.value
})

// Check if we have any local images (uploaded or event spaces)
const hasLocalImages = computed(() => restaurantImageIds.value.length > 0)

// Check if we have cached Google Place photos
const hasCachedGooglePhotos = computed(() => {
  return props.restaurant?.googlePlaceDetails?.photos?.length > 0
})

// Check if we can fetch Google Places photos (has placeId but no cached photos)
const canFetchGooglePhotos = computed(() => {
  const googlePlaceId = props.restaurant?.googlePlaceId
  
  return googlePlaceId && !hasLocalImages.value && !hasCachedGooglePhotos.value
})

// Google photo URL (either from cached data or fetched on-demand)
const googlePhotoUrl = computed(() => {
  // Fetched on-demand photo takes priority (used as fallback when cached URL failed)
  if (fetchedGooglePhotoUrl.value) {
    return fetchedGooglePhotoUrl.value
  }

  // Use cached Google photos (skip if previously failed to load)
  if (!imageLoadFailed.value && props.restaurant?.googlePlaceDetails?.photos?.length > 0) {
    return getPhotoUrl(props.restaurant.googlePlaceDetails.photos[0].photo_reference, 800, 800)
  }

  return null
})

// Handle image load failure - attempt fresh Google Places fetch
const onImageError = () => {
  const googlePlaceId = props.restaurant?.googlePlaceId
  if (googlePlaceId && !hasFetchedGooglePhoto.value) {
    imageLoadFailed.value = true
    fetchGooglePlacePhoto({ force: true })
  }
}

// Fetch Google Places photo on-demand
const fetchGooglePlacePhoto = async ({ force = false } = {}) => {
  if (isFetchingGooglePhoto.value || hasFetchedGooglePhoto.value) return
  if (!force && !canFetchGooglePhotos.value) return

  const googlePlaceId = props.restaurant?.googlePlaceId
  if (!googlePlaceId) return

  isFetchingGooglePhoto.value = true
  try {
    const placeDetails = await googlePlacesService.getPlaceDetails(googlePlaceId, ['photos'])
    if (placeDetails?.photos?.length > 0) {
      const photoRef = placeDetails.photos[0].photo_reference
      if (photoRef) {
        fetchedGooglePhotoUrl.value = getPhotoUrl(photoRef, 800, 800)
      }
    }
  } catch (error) {
    console.error('Failed to fetch Google Places photo:', error)
  } finally {
    isFetchingGooglePhoto.value = false
    hasFetchedGooglePhoto.value = true
  }
}

// Assuming you have a reactive property for foundPlace and showAllPhotos
const filteredPhotos = computed(() => {
  if (showAllPhotos.value) return foundPlace.value.photos
  
  return foundPlace.value.photos.slice(0, 3) // Only return the first three photos
})

//cleanURL()
const cleanUrl = computed(() => {
  if (foundPlace.value?.website) {
    let url = foundPlace.value.website
    url = url.replace(/(^\w+:|^)\/\//, '')
    url = url.replace(/\/$/, "")

    //remove ? and anything after it
    url = url.split('?')[0]

    return url
  }
})

// Extracted function for updating restaurant images
// Uses function declaration (hoisted) so watchers with { immediate: true } can call it during setup
async function updatePlaceData() {
  // Reset all local state to prevent data carry-over from previous restaurant
  foundPlace.value = null
  todaysHours.value = null
  showAllHours.value = false
  showAllPhotos.value = false
  showFullDescription.value = false
  imageLoadFailed.value = false
  fetchedGooglePhotoUrl.value = null
  hasFetchedGooglePhoto.value = false
  isFetchingGooglePhoto.value = false

  if (!props.restaurant) return

  // Try to find restaurant in store, but prefer props.restaurant if it has more complete data
  // This handles the case where localForage cache has stale data without googlePlaceDetails
  // Use strict equality and require a truthy ID to avoid `undefined === undefined` false matches
  // (bulk-fetched restaurants via .lean() don't include the Mongoose virtual `id` field)
  const restaurantIdToFind = props.restaurant?._id || props.restaurant?.id
  let storeRestaurant = restaurantIdToFind
    ? restaurants.value.find(r => r._id === restaurantIdToFind || r.id === restaurantIdToFind)
    : null

  // Prefer props.restaurant if it has googlePlaceDetails and storeRestaurant doesn't
  // This ensures fresh API data takes precedence over stale localForage cache
  let theRestaurant
  if (props.restaurant?.googlePlaceDetails && !storeRestaurant?.googlePlaceDetails) {
    theRestaurant = props.restaurant
  } else {
    theRestaurant = storeRestaurant || props.restaurant
  }

  // If restaurant data is missing googlePlaceDetails (bulk endpoints exclude it to reduce payload),
  // fetch the full restaurant data by ID. This ensures hours, address, phone etc. are available.
  const hasFullDetails = theRestaurant?.googlePlaceDetails?.opening_hours || theRestaurant?.googlePlaceDetails?.vicinity
  const idToFetch = theRestaurant?._id || theRestaurant?.id
  // Only attempt the full-details fetch once per restaurant. getRestaurantById()
  // reassigns googlePlaceDetails to a new object, which retriggers the watcher that
  // calls updatePlaceData(); if the fetched data is still missing opening_hours and
  // vicinity (e.g. stripped by an admin save), hasFullDetails never becomes true and
  // this would loop forever. The guard breaks the cycle after a single fetch.
  if (theRestaurant && !hasFullDetails && idToFetch && !fullDetailsFetchedIds.has(idToFetch)) {
    fullDetailsFetchedIds.add(idToFetch)
    try {
      const fullRestaurant = await restaurantsStore.getRestaurantById(idToFetch)
      if (fullRestaurant?.googlePlaceDetails) {
        theRestaurant = fullRestaurant
      }
    } catch (error) {
      console.warn('[RestaurantProfile] Failed to fetch full restaurant data:', error)
    }
  }

  if (theRestaurant) {
    // ✅ Use cached data from MongoDB instead of API call
    const cachedData = theRestaurant.googlePlaceDetails

    if (cachedData) {
      console.log('[RestaurantProfile] Processing cached data:', {
        hasWebsite: !!cachedData.website,
        websiteType: typeof cachedData.website,
        hasUrl: !!cachedData.url,
        urlType: typeof cachedData.url,
        hasPhotos: !!cachedData.photos,
        photosLength: cachedData.photos?.length
      })

      // Deep clone and normalize the data to ensure all values are primitives where expected
      // IMPORTANT: Prefer database address over Google's abbreviated vicinity
      foundPlace.value = {
        place_id: cachedData.place_id,
        name: cachedData.name,
        vicinity: theRestaurant.address || cachedData.vicinity,
        formatted_phone_number: cachedData.formatted_phone_number,
        website: typeof cachedData.website === 'string' ? cachedData.website : (cachedData.website?.url || null),
        url: typeof cachedData.url === 'string' ? cachedData.url : `https://maps.google.com/?q=place_id:${cachedData.place_id}`,
        opening_hours: cachedData.opening_hours ? {
          weekday_text: Array.isArray(cachedData.opening_hours.weekday_text)
            ? cachedData.opening_hours.weekday_text
            : []
        } : null,
        photos: [],
        geometry: null
      }

      console.log('[RestaurantProfile] Normalized foundPlace:', {
        websiteType: typeof foundPlace.value.website,
        urlType: typeof foundPlace.value.url,
        website: foundPlace.value.website,
        url: foundPlace.value.url
      })

      // Normalize geometry structure
      if (cachedData.geometry) {
        const lat = cachedData.geometry.location?.lat ?? cachedData.geometry.lat
        const lng = cachedData.geometry.location?.lng ?? cachedData.geometry.lng

        if (lat && lng) {
          foundPlace.value.geometry = { lat, lng }

          // Update map center and markers
          center.value = { lat, lng }
          markers.value = [{
            position: { lat, lng }
          }]
        }
      }

      // Add helper methods for photo URLs
      if (Array.isArray(cachedData.photos)) {
        foundPlace.value.photos = cachedData.photos.map(photo => ({
          photo_reference: photo.photo_reference,
          height: photo.height,
          width: photo.width,
          html_attributions: Array.isArray(photo.html_attributions) ? photo.html_attributions : [],
          getUrl: (options = {}) => {
            const maxWidth = options.maxWidth || 400
            const maxHeight = options.maxHeight || 400
            
            return getPhotoUrl(photo.photo_reference, maxWidth, maxHeight)
          }
        }))
      }
    } else {
      foundPlace.value = null
    }

    // Fallback to direct fields if googlePlaceDetails is missing
    if (!foundPlace.value) {
      foundPlace.value = {
        name: theRestaurant.name,
        website: theRestaurant.website,
        vicinity: theRestaurant.address,
        formatted_phone_number: theRestaurant.phoneNumber,
        opening_hours: theRestaurant.openingHours ? { weekday_text: theRestaurant.openingHours } : null,
        url: `https://maps.google.com/?q=place_id:${theRestaurant.googlePlaceId}`,
        photos: []
      }
      console.warn('[RestaurantProfile] Missing googlePlaceDetails for:', {
        name: theRestaurant.name,
        market: theRestaurant.market,
        googlePlaceId: theRestaurant.googlePlaceId
      })
    } else {
      // Fill in any missing fields from restaurant data (googlePlaceDetails may be incomplete,
      // e.g. preferred endpoint only includes .photos)
      if (!foundPlace.value.name && theRestaurant.name) {
        foundPlace.value.name = theRestaurant.name
      }
      if (!foundPlace.value.vicinity && theRestaurant.address) {
        foundPlace.value.vicinity = theRestaurant.address
      }
      if (!foundPlace.value.formatted_phone_number && theRestaurant.phoneNumber) {
        foundPlace.value.formatted_phone_number = theRestaurant.phoneNumber
      }
      if (!foundPlace.value.website && theRestaurant.website) {
        foundPlace.value.website = theRestaurant.website
      }
      if (!foundPlace.value.opening_hours && theRestaurant.openingHours) {
        foundPlace.value.opening_hours = { weekday_text: theRestaurant.openingHours }
      }
    }

    // Only set if we don't have it yet to avoid redundant updates
    if(!props.restaurant?.googlePlaceData || (foundPlace.value && !props.restaurant.googlePlaceData.place_id && foundPlace.value.place_id)){
      props.restaurant.googlePlaceData = foundPlace.value
    }
    if(myPermission.value){
      showAllEventSpaces.value = true
    }
    await getStuff()

    // if(!justBookedReservationsByRestaurant.value?.[props.restaurant?.market]?.[props.restaurant?.name])
    //   await reservationsStore.getJustBookedReservationsByRestaurant(props.restaurant?.name, props.restaurant?.market)
  }

  // restaurantImageAttributionHtml.value = restaurantPlaceArray.value?.[0]?.photos?.[0]?.html_attributions?.[0]

  // Fetch Google Places photo on-demand if no local images available
  if (canFetchGooglePhotos.value) {
    fetchGooglePlacePhoto()
  }
}

const getStuff = async () => {
  await reservationsStore.getJustBookedReservationsByRestaurant(props.restaurant?.name, props.restaurant?.market)
  await happeningsStore.getHappeningsByRestaurant(props.restaurant?.name, props.restaurant?.market)
}

const recentBookings = computed(() => {
// go through justBookedReservationsByRestaurant and filter by reservationType == 'general'
  return justBookedReservationsByRestaurant?.value?.[props.restaurant?.market]?.[props.restaurant?.name]
})


const happeningsForThisRestaurant = computed(() => {
// go through justBookedReservationsByRestaurant and filter by reservationType == 'general'
  return happeningsByRestaurant?.value?.[props.restaurant?.market]?.[props.restaurant?.name]
})

const happeningsToShowUpcoming = computed(() => {
  return happeningsForThisRestaurant?.value?.filter(happening => (moment(happening.date).isSameOrAfter(moment().startOf('day')) || (happening.date == null)))
})

// Watcher to react to changes - watch the specific restaurant's googlePlaceDetails
// because the bulk getRestaurants() excludes this field, and it may be populated later
// by getRestaurantByProfileId() or getRestaurantById() via Object.assign
watch(() => {
  const restaurantId = props.restaurant?._id || props.restaurant?.id
  if (!restaurantId) return null
  const storeRestaurant = restaurants.value?.find(r => r._id === restaurantId || r.id === restaurantId)

  return storeRestaurant?.googlePlaceDetails
}, (newDetails, oldDetails) => {
  // Trigger when details appear or when partial data (photos-only from bulk) is replaced with full data
  const hadFullDetails = oldDetails?.opening_hours || oldDetails?.vicinity
  if (newDetails && (!oldDetails || !hadFullDetails)) {
    updatePlaceData()
  }
}, { immediate: false })



// Helper function to get day name from index (0 = Monday, 6 = Sunday)
const getDayName = index => {
  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
  
  return days[index]
}

// Computed property for formatted opening hours (handles both formats)
const formattedOpeningHours = computed(() => {
  // First check if we have Google Places data (has day names in the string)
  if (foundPlace.value?.opening_hours?.weekday_text && Array.isArray(foundPlace.value.opening_hours.weekday_text)) {
    // Check if the first entry has a day name (like "Monday:")
    const firstEntry = foundPlace.value.opening_hours.weekday_text[0]
    if (firstEntry && firstEntry.match(/^(Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday):/)) {
      // This is proper Google Places format with day names
      return foundPlace.value.opening_hours.weekday_text
    } else {
      // This is direct data without day names, format it
      return foundPlace.value.opening_hours.weekday_text.map((hours, index) => {
        const dayName = getDayName(index)
        
        return `${dayName}: ${hours.trim()}`
      })
    }
  }
  
  // Otherwise check for direct openingHours data
  if (props.restaurant?.openingHours && Array.isArray(props.restaurant.openingHours)) {
    return props.restaurant.openingHours.map((hours, index) => {
      const dayName = getDayName(index)
      
      return `${dayName}: ${hours.trim()}`
    })
  }
  
  return null
})

const getTodaysHours = () => {
  const fullHours = formattedOpeningHours.value
  if (!fullHours) return null
  
  // Get today's day of week (0 = Sunday in JS, but we need 0 = Monday)
  const today = moment().format('dddd')
  let todaysHours = fullHours.find(day => day.includes(today))
  todaysHours = todaysHours?.replace(today, 'Today').replace(":", "") || null
  
  return todaysHours
}

const shouldShowDescriptionToggle = computed(() => {
  if (!props.restaurant?.description) return false

  // Show toggle if description is longer than approximately 3 lines (roughly 200 characters)
  return props.restaurant.description.length > 200
})

// Prett sure this is not needed and just causing extra calls
// watch([() => props.restaurant, () => restaurantUserGroups.value], ([newRestaurant, newUserGroups], [oldRestaurant, oldUserGroups]) => {
//   //check if restaurant name/market is changing
//   console.log("22222")
//   updatePlaceData();
// }, { immediate: true });

const link = computed(() => {
  return `${window.location.origin}/r/${props.restaurant.profileId}`
})

const webShareApiSupported = computed(() => {
  return navigator.share
})

const shareLink = () => {
  if (navigator.share) {
    navigator.share({
      // title: 'Check out the restaurant on RM Preferred Reservations',
      // text: "Hey!" + "\n" + "I found this restaurant on RM." + "\n" +
      //     "Members use RM to get preferred times N/A on OpenTable, Resy, etc (i.e. restaurant’s private inventory) & to simplify private/group event bookings with 1 inquiry to 10 preferred places.\n" +
      //     "\n" +
      //     "Check it out:",
      url: link.value,
    })
      .then(() => console.log('Successful share'))
      .catch(error => console.log('Error sharing', error))
  }
}

const restaurantFansHeartRef = ref(null)
const showRestaurantFans = () => restaurantFansHeartRef.value?.openFavoritedBy()

onMounted(() => {
  updatePlaceData()

  // Record profile view
  const rid = props.restaurant?._id || props.restaurant?.id
  if (rid) {
    restaurantsStore.recordProfileView(rid)
  }
})

const copiedToClipboard = ref(false)
const copyPageURL = () => {
  let textArea = document.createElement("textarea")
  textArea.value = link.value
  document.body.prepend(textArea)
  textArea.focus()
  textArea.select()
  copiedToClipboard.value = !copiedToClipboard.value

  try {
    let successful = document.execCommand('copy')
    let msg = successful ? 'successful' : 'unsuccessful'
    console.log('Fallback: Copying text command was ' + msg)
  } catch (err) {
    console.error('Fallback: Oops, unable to copy', err)
  }

  document.body.removeChild(textArea)
}
</script>


<template>
  <!-- Prevent access to unlisted restaurants -->
  <div
    v-if="restaurant?.status === 'Unlisted'"
    class="content-card max-content-width mt-3"
  >
    <div class="text-center pa-8">
      <h2>Restaurant Not Available</h2>
      <p>This restaurant profile is not currently available.</p>
    </div>
  </div>
  
  <div v-else>
    <ClaimRestaurantPageDialog
      v-model:is-dialog-visible="isClaimRestaurantPageDialogOpen"
      :restaurant="restaurant"
    />
    <!--
      ONE gutter for the whole card (Kapil + the margin-fix mockups,
      2026-09-22). The photo, the "+", the tab bar, every divider and every
      tab's content share this left AND right inset, so nothing shifts
      sideways when the tab changes.
    -->
    <div class="content-card max-content-width mt-3 overflow-hidden restaurant-profile-card">
      <!-- Image section - show uploaded/event space images if available -->
      <div
        v-if="restaurantImageIds?.length > 0"
        class="restaurant-profile-masthead-container"
      >
        <div
          class="restaurant-profile-masthead"
          @click="imageDialog = true"
        >
          <img
            :src="mediaUrl(restaurantImageIds[0], 'regular')"
            alt="Restaurant Profile Image"
            class="restaurant-profile-masthead-bkg-image"
          >
          <div class="restaurant-profile-masthead-content">
            <div class="restaurant-profile-masthead-content-text" />
            <VBtn
              density="comfortable"
              class="rounded-sm view-all-photos-btn"
              variant="text"
              size="small"
            >
              View all ({{ restaurantImageIds?.length }})
            </VBtn>
          </div>
        </div>
      </div>
      <!-- Fallback to Google Places photo when no local images -->
      <div
        v-else-if="googlePhotoUrl"
        class="restaurant-profile-masthead-container"
      >
        <div class="restaurant-profile-masthead">
          <img
            :src="googlePhotoUrl"
            alt="Restaurant Profile Image"
            class="restaurant-profile-masthead-bkg-image"
            @error="onImageError"
          >
          <div class="restaurant-profile-masthead-content">
            <div class="restaurant-profile-masthead-content-text" />
          </div>
        </div>
      </div>

      <div
        v-if="foundPlace"
        class="restaurant-profile-body"
        :style="{ '--rp-indent': (!editingReservation && !!restaurant.acceptingReferrals) ? '42px' : '0px' }"
      >
        <!--
          ONE left edge for the whole page (Kapil, 2026-09-22). The name sets
          it; "Favorited by", views, the tabs and everything under them line up
          with it, and the "+" hangs in the gutter beside the header rather
          than pushing the text in. `--rp-indent` is that edge, and it closes
          up to the page gutter when there is no "+" to make room for — the old
          37px was hardcoded and stayed put either way.
        -->
        <div class="d-flex align-start restaurant-profile-identity mb-2">
          <AddToRequestButton
            v-if="!editingReservation && !!restaurant.acceptingReferrals"
            class="restaurant-profile-add"
            @click="newDraft(restaurant.market, restaurant.name)"
          />
          <div class="flex-grow-1 restaurant-profile-identity__title">
            <!--
              THE identity cluster, the same component Discover, Search and the
              AI rows use (Kapil, 2026-09-22: "match what we do on the Discover
              Page"). The page used to hand-roll its own — a green ribbon SVG
              before the name, the name tinted green, and the view count in the
              group's line. Name, group, then Preferred as a plain tappable
              word: one markup structure, one scale, no drift.
            -->
            <RestaurantTitleBlock
              ref="restaurantFansHeartRef"
              :restaurant="restaurant"
              :name="restaurant.name"
              :market="restaurant.market"
              :group="restaurant.group"
              :status="restaurant.status"
              wrap
            />
          </div>

          <div class="ml-auto restaurant-profile-header-actions">
            <div class="d-flex align-center">
              <VBtn
                v-if="webShareApiSupported"
                density="comfortable"
                variant="text"
                size="small"
                icon="ion-share-outline"
                @click="shareLink"
              />
              <VBtn
                v-else
                density="comfortable"
                variant="text"
                size="small"
                icon="ion-share-outline"
                @click="copyPageURL"
              />

              <VSnackbar
                v-model="copiedToClipboard"
                :timeout="2000"
              >
                Url copied to clipboard
              </VSnackbar>
              <VBtn
                v-if="myUser?.userLevel == 'admin' || myPermission == 'admin' || myPermission == 'owner'"
                size="small"
                density="comfortable"
                class="pa-0"
                style="padding: 0; min-width: 0;"
                variant="text"
                icon="mdi-cog"
                @click="restaurantsStore.restaurantEditRestaurant(restaurant, foundPlace)"
              />
            </div>
          </div>
        </div>
        <!-- Member Preferred status banner -->
        <div
          v-if="isMemberPreferred"
          class="member-preferred-banner"
        >
          <VIcon
            icon="mdi-star-check"
            size="16"
            class="mr-1"
          />
          <template v-if="memberPreferredGroupName">
            <!-- Interpolated string: $t() picks up CMS values via mergeLocaleMessage. Static base copy lives under restaurant.preferred.bannerTitleViaGroup. -->
            <span>{{ $t('restaurant.preferred.bannerTitleViaGroup', { groupName: memberPreferredGroupName }) }}</span>
          </template>
          <template v-else>
            <CmsText
              k="restaurant.preferred.bannerTitle"
              tag="span"
            />
          </template>
        </div>
        <FriendsLikedChips
          :restaurant-id="restaurantId"
          class="friends-liked-chips-profile rp-aligned"
          @click="showRestaurantFans"
        />

        <!--
          Views sit under "Favorited by" (Kapil, 2026-09-22). They belong to
          the restaurant's standing, not to its name — in the group's line they
          read as part of who owns the place.
        -->
        <div
          v-if="profileViewCount > 0"
          class="restaurant-profile-views rp-aligned"
        >
          <span
            class="d-inline-flex align-center profile-views-trigger"
            role="button"
            tabindex="0"
            @click="profileViewsDialog = true"
            @keydown.enter="profileViewsDialog = true"
            @keydown.space.prevent="profileViewsDialog = true"
          >
            <VIcon
              size="12"
              class="mr-1"
            >
              mdi-poll
            </VIcon>
            {{ profileViewCount.toLocaleString() }}
          </span>
        </div>

        <div style="display: flex;">
          <VTabs
            v-if="foundPlace"
            v-model="tab"
            class="tab-height"
            center-active
          >
            <VTab
              v-show="hasInstantBookings"
              value="PlatinumReserved"
              class="tab-height"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style="margin-right: 4px;"
              >
                <path
                  d="M13 2L4.09 12.11C3.69 12.59 3.89 13.34 4.49 13.53L10 15.5L9 22L17.91 11.89C18.31 11.41 18.11 10.66 17.51 10.47L12 8.5L13 2Z"
                  fill="#FFD700"
                  stroke="#B8860B"
                  stroke-width="0.5"
                />
              </svg>
              Instant Bookings
            </VTab>
            <VTab
              value="Details"
              class="tab-height"
            >
              Details
            </VTab>
            <VTab
              v-show="!!restaurant.acceptingReferrals && (myPermission || restaurant.eventSpaces?.length > 0)"
              value="Events"
              class="tab-height"
            >
              Group/Private
            </VTab>

            <!--
              Member Highlights is its own tab (Kapil, 2026-09-21): it sat above
              the Details content and pushed the restaurant's own facts — the
              address, the hours — below the fold. The tab appears only when
              there is something in it for this viewer, which the server
              decides (`hasHighlights` comes back from the highlights fetch).
            -->
            <VTab
              v-show="myUser && myUser.userLevel !== 'dummy' && (hasMemberHighlights || isRestaurantTeam)"
              value="MemberHighlights"
              class="tab-height"
            >
              Member Highlights
            </VTab>

            <VTab
              v-show="myUser && myUser.userLevel !== 'dummy'"
              value="MyNote"
              class="tab-height"
            >
              <VIcon
                size="14"
                style="margin-right: 4px;"
                :icon="hasNote ? 'mdi-note-text' : 'mdi-note-text-outline'"
              />
              My Notes
            </VTab>

          <!--
            <v-tab v-show="!!restaurant.acceptingReferrals && (myPermission || happeningsToShowUpcoming?.length > 0)" value="Happenings" class="tab-height">
            <v-icon style="margin-left: -3px;">emojione-v1:lightning-mood</v-icon>Happenings
            </v-tab>
          -->
          </VTabs>
        </div>
        <VDivider />
        <VWindow
          v-if="foundPlace"
          v-model="tab"
        >
          <VWindowItem
            class=""
            value="Events"
          >
            <VExpandTransition>
              <EventSpacesViewerAndEditor
                v-if="showAllEventSpaces"
                :restaurant="restaurant"
              />
            </VExpandTransition>
          </VWindowItem>
          <VWindowItem
            class="pt-4"
            value="PlatinumReserved"
          >
            <div class="w-100 px-2 px-sm-0">
              <InstantBookingSlotsCompact :restaurant="restaurant" />
            </div>
          </VWindowItem>
          <!--
            <v-window-item class="pt-4" value="Happenings">
            <div class="w-100 pa-4">
            <HappeningViewerAndEditor :restaurant="restaurant"/>
            </div>
            </v-window-item> 
          -->
          <!--
            `eager` on purpose: a VWindowItem mounts lazily, so the component
            would never load while its own tab was hidden, and the tab is
            hidden until the component says it has something. Eager breaks that
            circle — it loads once, then the tab appears if there is anything.
          -->
          <VWindowItem
            class=""
            value="MemberHighlights"
            eager
          >
            <RestaurantMemberHighlights
              v-if="restaurant?._id"
              :restaurant-id="String(restaurant._id)"
              :refresh-key="highlightsVersion"
              :show-empty="isRestaurantTeam"
              class="pt-4"
              @loaded="hasMemberHighlights = $event"
            />
          </VWindowItem>
          <VWindowItem
            class=""
            value="Details"
          >
            <div class="restaurant-profile-body-about pt-6">
              <div class="restaurant-profile-body-about-text">
                <span
                  v-if="!restaurant.acceptingReferrals"
                  class="text-xs d-block mb-1"
                >🚫 not accepting requests via RM</span>
                <div
                  v-else-if="restaurant.status == 'Preferred'"
                  class="text-xs d-block mb-1 restaurant-profile-details-header"
                >
                  <div>
                    <span class="text-primary">{{ restaurant.name }} is a </span>
                    <!--
                      Opens the For Restaurants modal in place, the same one
                      the Preferred word opens on every restaurant row (Kapil,
                      2026-09-22). It used to navigate away to /preferred.
                    -->
                    <button
                      type="button"
                      class="restaurant-profile-preferred-label"
                      aria-label="About Preferred restaurants"
                      @click.stop="usersStore.isShowingRestaurantsInfoDialog = true"
                    >
                      Preferred Restaurant
                    </button>
                  </div>
                  <span v-if="restaurant.sourceReferredBy && restaurant.sourceMemberProfileId"> - thanks to <a
                    :href="`/m/${restaurant.sourceMemberProfileId}`"
                    target="_blank"
                  >{{ restaurant.sourceReferredBy }}</a></span>
                  <span v-else-if="restaurant.sourceReferredBy">(thanks to {{ restaurant.sourceReferredBy }})</span>
                </div>
                <div class="restaurant-profile-details-content">
                  <span class="text-xs d-block mb-1">{{ foundPlace.vicinity }}</span>
                  <span class="text-xs d-block mb-1"><a :href="`tel:${foundPlace.formatted_phone_number}`">{{ displayPhone(foundPlace.formatted_phone_number) }}</a></span>
                  <span
                    v-if="foundPlace.website"
                    class="text-xs d-block mb-3"
                  ><a
                    :href="foundPlace.website"
                    target="_blank"
                  >{{ cleanUrl }} <VIcon size="x-small">mdi-external-link</VIcon></a></span>
                </div>

                <template v-if="restaurant.description">
                  <div class="text-xs mb-2">
                    <div
                      :class="{'description-truncated': !showFullDescription}"
                      style="white-space: pre-wrap;"
                    >
                      {{ restaurant.description }}
                    </div>
                    <span
                      v-if="shouldShowDescriptionToggle"
                      class="text-primary cursor-pointer"
                      @click="showFullDescription = !showFullDescription"
                    >
                      {{ showFullDescription ? 'view less' : 'view more' }}
                    </span>
                  </div>
                </template>

                <div class="restaurant-profile-details-content">
                  <h5 class="mb-1 mt-4">
                    Hours
                  </h5>
                  <div
                    v-if="formattedOpeningHours"
                    class="restaurant-hours-holder"
                    @click="showAllHours = !showAllHours"
                  >
                    <span
                      v-if="getTodaysHours() && !showAllHours"
                      class="text-xs d-inline-block"
                    >{{ getTodaysHours() }}</span>
                    <div v-else>
                      <span
                        v-for="day in formattedOpeningHours"
                        :key="day"
                        class="text-xs d-block mb-1"
                      >{{ day }}</span>
                    </div>
                    <VBtn
                      density="compact"
                      size="small"
                      class="restaurant-hours-toggle"
                      variant="text"
                      color="secondary"
                      :icon="!showAllHours?'mdi-chevron-down':'mdi-chevron-up'"
                      @click="(e) => {e.stopPropagation(); showAllHours = !showAllHours;}"
                    />
                  </div>

                  <div v-if="!!restaurant.acceptingReferrals && recentBookings?.length > 0">
                    <h5 class="mb-1 mt-4">
                      Member Bookings<VBtn
                        density="compact"
                        size="small"
                        class="map-toggle"
                        variant="text"
                        color="secondary"
                        :icon="!showBookings?'mdi-chevron-down':'mdi-chevron-up'"
                        @click="(e) => {e.stopPropagation(); showBookings = !showBookings;}"
                      />
                    </h5>
                    <template v-if="myUser?.isPlatinum">
                      <template v-if="showBookings">
                        <div class="pt-2">
                          <div
                            v-if="recentBookings?.length === 0"
                            class="text-center text-xs"
                          >
                            no Preferred Reservations booked yet
                          </div>
                          <MemberBookingCard
                            v-for="(item, index) in recentBookings"
                            :key="index"
                            :booking="item"
                          />
                        </div>
                      </template>
                    </template>
                    <p
                      v-else
                      class="text-xs text-grey mt-1"
                    >
                      Viewable by Platinum members only
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </VWindowItem>
          <VWindowItem
            class="pt-4"
            value="MyNote"
          >
            <ProfileEntriesPanel
              v-if="restaurantId"
              :restaurant-id="restaurantId"
              :restaurant-name="restaurant?.name || ''"
              @changed="highlightsVersion++"
            />
          </VWindowItem>
        </VWindow>
      </div>
      <VDialog
        v-model="imageDialog"
        class="image-dialog"
      >
        <VCard class="image-dialog-content">
          <VCarousel
            v-model="currentImage"
            height="80dvh"
            hide-delimiters
            show-arrows="hover"
          >
            <VCarouselItem
              v-for="(photo, index) in restaurantImageIds"
              :key="index"
              :src="mediaUrl(photo, 'regular')"
              :value="photo"
              contain
            />
          </VCarousel>
          <VBtn
            class="image-dialog-close-button"
            density="default"
            variant="text"
            color="#ffffff"
            icon="mdi-close"
            @click="imageDialog = false"
          />
        </VCard>
      </VDialog>
      <VDialog
        max-width="500"
        :model-value="isInfoDialogVisible"
        fullscreen
      >
        <VCard class="text-center px-0 py-0 pb-0 d-inline">
          <PreferredContent :show-search="false" />
        </VCard>
        <VBtn
          icon="$close"
          size="large"
          variant="text"
          color="secondary"
          class="dialog-close-btn"
          @click="isInfoDialogVisible = false"
        >
          <VIcon>mdi-close</VIcon>
        </VBtn>
      </VDialog>
      <VDialog
        v-model="profileViewsDialog"
        max-width="320"
      >
        <VCard class="pa-4 text-center">
          <div class="text-body-1">
            Restaurant profile views
          </div>
          <VBtn
            class="mt-4"
            color="primary"
            variant="flat"
            block
            @click="profileViewsDialog = false"
          >
            Close
          </VBtn>
        </VCard>
      </VDialog>
    </div>
  </div>
</template>


<style lang="scss" scoped>
.dialog-close-btn {
  position: fixed;
  right: 6px;
  top: 6px;
}

.profile-views-trigger {
  cursor: pointer;
}

.profile-container {
  display: flex;
  flex-direction: column;
  align-items: start;
  max-width: 460px;
  margin: 1rem auto 0.5rem;
  border: thin solid var(--ds-border-light);
  border-radius: var(--ds-radius-sm);
}
.profile-header {
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: space-between;
}


@media screen and (min-width: 670px) {
  .profile-container {
    flex-direction: row;
    align-items: start;
  }

  .profile-header {
    width: auto;
  }
}



.avatar {
  width: 6rem;
  height: 6rem;
  background: var(--ds-brand-primary-light);
  outline: thin solid rgba(69, 182, 169, 0.25);
  outline-offset: 0.25rem;
}

@media screen and (min-width: 670px) {
  .avatar {
    width: 9rem;
    height: 9rem;
    outline-offset: 0.5rem;
    margin: 1rem 2.25rem 0 0;
  }
}

.avatar-fallback {
  width: 100%;
  height: 100%;
  font-size: 1.6rem;
}

.avatar-image {
  border-radius: var(--ds-radius-full);
  width: 100%;
  height: 100%;
  object-fit: cover;
}

@media screen and (min-width: 670px) {
  .avatar-fallback {
    font-size: 3rem;
  }
}

.avatar-fallback-text {
  width: fit-content;
}

.profile-header-stats {
  gap: 0.7rem;
}

@media screen and (min-width: 670px) {
  .profile-header-stats {
    display: none;
  }

}

.verified-icon {
  color: #45b67e;
  width: 1.25rem;
  height: 1.25rem;
}

.verified-icon-pending {
  cursor: pointer;
  color: #ffd900;
  width: 1.25rem;
  height: 1.25rem;
}
.verified-pending-text{
  cursor: pointer;
  display: inline-block;
  padding: 0.1rem;
  color: #5596d6;
  font-size: 0.75rem;
  padding: 0.1em 0.5em;
  border-radius: 0.4em;
  margin-left: -0.5em;
}

.verified-pending-text:hover {
  color: #21609f;
  background: #f1f1f1;
}

.profile-info {
  display: flex;
  flex-direction: row;
  align-items: start;
  flex-wrap: wrap;
  gap: 1rem
}

.profile-info-stats {
  display: none;
}

@media screen and (min-width: 670px) {
  .profile-info-stats {
    display: flex;
    gap: 2rem;
  }
}

.user-bio {
  max-width: 100ch;
}


@media screen and (min-width: 670px) {
  .action-buttons {
    justify-content: start;
    align-items: center;
    position: static;
    border-top: none;
    box-shadow: none;
  }

  .profile-actions-edit-button {
    display: none;
  }
}

.restaurant-profile-masthead-container {
  padding: var(--rp-gutter) var(--rp-gutter) 0;
}

.restaurant-profile-masthead {
  position: relative;
  cursor: pointer;
  border-radius: 0.75rem;
  overflow: hidden;
}

.restaurant-profile-masthead-bkg-image {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 0.75rem;
}

.restaurant-profile-masthead-content {
  padding: 12rem 0.5rem 0.5rem;
  display: flex;
  flex-direction: row;
  align-items: end;
  justify-content: space-between;
  isolation: isolate;
  column-gap: 1rem;
  width: 100%;
  aspect-ratio: 4;
  background: linear-gradient(0deg, rgba(0,0,0,0.55) 20%, rgba(0,0,0,0) 40%, rgba(0,0,0,0)),linear-gradient(45deg, rgba(0,0,0,0.25) 20%, rgba(0,0,0,0) 50%, rgba(0,0,0,0));
}

@media only screen and (min-width: 430px){
  .restaurant-profile-masthead-content {
    flex-direction: row;
    align-items: center;
    padding: 20rem 1rem 1rem 1rem;
  }
}

.restaurant-profile-masthead-content-text {
  cursor: default;
}

.restaurant-profile-masthead-title {
  margin-top: 0.5rem;
  color: var(--ds-text-on-dark);
}

.restaurant-profile-preferred-label {
  background: none;
  border: 0;
  padding: 0;
  cursor: pointer;
  font-family: inherit;
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--ds-brand-primary-dark);
  text-decoration: none;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }
}

.view-all-photos-btn {
  background-color: transparent !important;
  color: var(--ds-text-on-dark);
  text-shadow: 0 1px 3px rgba(0,0,0,0.3);
}

.description-truncated {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.restaurant-profile-masthead-info {
  color: rgba(255,255,255, 0.9);
}

.restaurant-profile-body-about {
  display: flex;
  flex-direction: column;
  flex-wrap: wrap-reverse;
  align-items: start;
  gap: 1.5rem;
}

.restaurant-profile-body-about-text {
  width: 100%;
  flex: 1;
}

.restaurant-profile-body-images {
  flex: 2;
  width: 100%;
}

  .restaurant-profile-body-image:hover {
    transform: scale(1.05);
  }

  .restaurant-profile-body-images-grid {
    display: grid;
    gap: 0.6rem;

    grid-template-columns: repeat(3, 1fr);
    grid-auto-flow: dense;
  }

/* Media query for phones and tablets */
@media (max-width: 2468px) { /* Adjust 768px based on your breakpoint needs */
  .restaurant-profile-body-images-grid {
    /* Ensure 3 items per row on smaller screens */
    grid-template-columns: repeat(3, 1fr);
  }
}

  .restaurant-profile-body-image {
    height: 100px;
    width: 100%;
    object-fit: cover;
    border-radius: 0.2rem;
    cursor: pointer;
    transform: translate3d(0,0,0);
    transition: transform var(--ds-duration-normal) ease-in-out;
    will-change: auto;
  }


.restaurant-profile-masthead-map-holder {
  position: relative;
  width: 100%;
  max-width: 35rem;
}

.restaurant-profile-masthead-map {
  width: 100%;
  overflow: hidden;
  border-radius: 0.3rem;
  flex-shrink: 0;
  border: 1px solid var(--ds-border-default);
}

.restaurant-profile-masthead-map-button {
  position: absolute;
  isolation: isolate;
  z-index: var(--ds-z-dropdown);
  left: 0.8rem;
  color: #2196f3;
  top: 2.5rem;
}

.image-dialog {
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.image-dialog-content {
  background: black;
}

.image-dialog-close-button {
  position: absolute;
  right: 0.5rem;
  top: 0.5rem;
  color: var(--ds-text-on-dark);
  z-index: var(--ds-z-dropdown);
  background-color: rgba(0,0,0,0.4);
;}

.restaurant-hours-holder {
  cursor: pointer;
  display: flex;
  width: fit-content;
  border-radius: var(--ds-radius-sm);
}

.restaurant-hours-toggle {
  transform: translateY(-0.25em);
  margin-left: 0.5rem;
}

.private-event-space-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
}

/* Media query for phones and tablets */
@media (max-width: 868px) { /* Adjust 768px based on your breakpoint needs */
  .private-event-space-grid {
    /* Ensure 3 items per row on smaller screens */
    grid-template-columns: repeat(1, 1fr);
  }
}

.v-tab.v-tab {
  min-width: unset;
  padding-left: 0.6rem;
  padding-right: 0.6rem;
  font-size: 0.8rem;
}



.restaurant-profile-details-header {
  margin-bottom: 0.5rem;
}

/* The 24px here was "20px icon width + 4px gap" for the Preferred ribbon,
   which is gone — it was indenting the address, phone, website, Hours and
   Member Bookings past everything else on the card. */
.restaurant-profile-details-content {
  margin-left: 0;
}

/*
  ONE gutter for the card, from the spacing scale — the photo, the "+", the
  tab bar, the dividers and every tab's content share it, left and right.
  Vuetify's pa-2/pa-sm-6 used to put the body at 8px (24px on desktop) while
  the photo sat at 16px, which is where the card's second edge came from.
*/
.restaurant-profile-card {
  --rp-gutter: var(--ds-space-4);
}

.restaurant-profile-body {
  /* Air under the photo before the header row (Kapil, 2026-09-22). */
  padding: var(--ds-space-3) var(--rp-gutter) var(--rp-gutter);

  /*
    The hanging indent is the HEADER BLOCK's alone: the "+" sits in the gutter
    with the name, group, Preferred, "Favorited by" and views in a column
    beside it. From the tab bar down there is no indent at all, so Details,
    Member Highlights and My Notes start on the same edge as the photo.
  */
  --rp-indent: 42px;
}

.rp-aligned {
  margin-left: var(--rp-indent);
}

.restaurant-profile-identity {
  gap: 12px;
}

/*
  The name's column may shrink below the name's one-line width — a flex item
  defaults to min-width:auto, which let a long name push the card wider than
  the screen, clipping the name and hiding the heart and the share button
  (Kapil, 2026-09-23). The name wraps inside it instead (RestaurantTitleBlock
  `wrap`); the "+" and the actions keep their size.
*/
.restaurant-profile-identity__title {
  min-width: 0;
}

.restaurant-profile-identity .restaurant-profile-header-actions {
  flex-shrink: 0;
}

/*
  The "+" as it reads on Discover and the Bookings card: a white circle,
  outlined, with the plus in brand green (Kapil, 2026-09-22). The circle IS
  the button box, so its left edge is the card's gutter — internal padding
  used to hold it ~4px inside. The tap target is grown to 44px with a
  pseudo-element instead, which costs the layout nothing.

  Scoped to the body so it beats the component's own base rule: both are one
  class on the same element, where specificity ties and source order decides.
*/
.restaurant-profile-body .restaurant-profile-add {
  position: relative;
  width: 30px;
  min-width: 30px;
  height: 30px;
  padding: 0;
  border: 1.5px solid var(--ds-border-strong);
  border-radius: 50%;
  background: var(--ds-surface-card);
  color: var(--ds-brand-primary-dark);
  transition: transform 0.2s ease, border-color 0.2s ease;
}

.restaurant-profile-body .restaurant-profile-add:hover {
  border-color: var(--ds-brand-primary-dark);
  transform: scale(1.05);
}

.restaurant-profile-body .restaurant-profile-add :deep(svg) {
  width: 16px;
  height: 16px;
}

.restaurant-profile-body .restaurant-profile-add :deep(svg ellipse) {
  fill: transparent;
}

.restaurant-profile-body .restaurant-profile-add :deep(svg path) {
  stroke: var(--ds-brand-primary-dark);
  stroke-width: 1.4;
}

/*
  Share and gear: Vuetify's icon buttons carry 6px of padding around the
  glyph, so the gear's right edge sat 6px inside the photo's. The box is
  pulled out by exactly that, which aligns the glyph and leaves the tap
  targets untouched.
*/
.restaurant-profile-header-actions {
  margin-right: -6px;
}

/* A 44px tap target around a 30px circle, without moving anything. */
.restaurant-profile-body .restaurant-profile-add::after {
  content: '';
  position: absolute;
  inset: -7px;
}

.friends-liked-chips-profile {
  margin-top: -4px;
}

/* Directly under "Favorited by", on the same left edge. */
.restaurant-profile-views {
  margin-top: 2px;
  font-size: 12px;
  color: var(--ds-text-muted, rgba(var(--v-theme-on-surface), 0.6));
}

.member-preferred-banner {
  display: flex;
  align-items: center;
  gap: 4px;
  background: var(--ds-brand-primary-light);
  border-left: 3px solid var(--ds-brand-primary-dark);
  border-radius: 6px;
  padding: 8px 12px;
  margin: 8px 0 8px 10px;
  color: var(--ds-brand-primary-dark);
  font-size: 13px;
  font-weight: 500;
}
</style>
